import { Component, type ReactNode } from "react";

import * as Sentry from "@sentry/react";

import ErrorFallback from "./ErrorFallback";
import type {
  ErrorBoundaryProps,
  ErrorBoundaryState,
  ErrorInfo,
} from "./types";

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const enhancedErrorInfo: ErrorInfo = {
      componentStack: errorInfo.componentStack ?? "",
      errorBoundary: this.props.boundaryName || "Unknown",
    };

    this.setState({
      errorInfo: enhancedErrorInfo,
    });

    // Report to Sentry with additional context
    Sentry.withScope((scope) => {
      scope.setTag("errorBoundary", this.props.boundaryName || "unknown");
      scope.setContext("errorInfo", {
        componentStack: errorInfo.componentStack,
        boundaryName: this.props.boundaryName,
      });

      // Add additional context if available
      scope.setContext("location", {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      });

      Sentry.captureException(error);
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, enhancedErrorInfo);
    }

    // Log to console in development
    if (import.meta.env.VITE_APP_ENVIRONMENT === "development") {
      console.group("🚨 Error Boundary Caught Error");
      console.error("Error:", error);
      console.error("Error Info:", errorInfo);
      console.error("Boundary Name:", this.props.boundaryName || "Unknown");
      console.groupEnd();
    }
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use ErrorFallback component with specified mode
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={
            this.props.enableRetry !== false ? this.handleRetry : undefined
          }
          mode={this.props.fallbackMode || "detailed"}
          boundaryName={this.props.boundaryName}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
